import is from '@sindresorhus/is';
import { useCallback, useMemo, useState } from 'react';

import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';

import { useJobLevelDetailsQueries, useJobRoleDetailsQueries } from '../api/use-job-item-details';
import { useJobLevelVersionsQueries, useJobRoleVersionsQueries } from '../api/use-job-item-versions';
import type { ComparisonColumn } from '../profile-comparison/ProfileComparisonTable';
import { resolveWinningVersion } from '../profile-comparison/resolve-winning-version';
import { uniqueParentIds, useParentTitles } from './use-parent-titles';

export type ComparedItem = ComparisonColumn & {
    readonly details: JobLevelDetails | JobRoleDetails | undefined;
    // Cluster for a role column; parent role for a level column. Distinguishes same-named siblings.
    readonly parentTitle: string | undefined;
};

export type UseRoleComparisonDataResult = {
    readonly items: readonly ComparedItem[];
    readonly selectItemVersion: (itemId: string, version: string) => void;
};

const useComparedItemsOfType = (
    architectureId: string,
    itemType: 'job-level' | 'job-role',
    itemIds: readonly string[],
    selectedVersions: Readonly<Record<string, string>>,
) => {
    const roleIds = itemType === 'job-role' ? itemIds : [];
    const levelIds = itemType === 'job-level' ? itemIds : [];
    const roleVersionQueries = useJobRoleVersionsQueries({ architectureId, roleIds });
    const levelVersionQueries = useJobLevelVersionsQueries({ architectureId, levelIds });
    const versionQueries = itemType === 'job-role' ? roleVersionQueries : levelVersionQueries;
    const requests = useMemo(
        () =>
            itemIds.map((itemId, index) => {
                const versions = versionQueries[index]?.data;
                const version = selectedVersions[itemId] ?? resolveWinningVersion(versions);

                // An item without versions still has details; the backend then serves its default version.
                return { enabled: !is.undefined(versions), itemId, version };
            }),
        [itemIds, selectedVersions, versionQueries],
    );
    const roleDetailQueries = useJobRoleDetailsQueries({
        architectureId,
        roleRequests: itemType === 'job-role' ? requests : [],
    });
    const levelDetailQueries = useJobLevelDetailsQueries({
        architectureId,
        levelRequests: itemType === 'job-level' ? requests : [],
    });

    return {
        detailQueries: itemType === 'job-role' ? roleDetailQueries : levelDetailQueries,
        versionQueries,
    };
};

// Loads every compared item at its winning version; the versions list must resolve first so the
// details query asks for that exact version. Only an item without any versions falls back to the
// backend default.
export const useRoleComparisonData = (
    architectureId: string,
    itemType: 'job-level' | 'job-role',
    itemIds: readonly string[],
): UseRoleComparisonDataResult => {
    const [selectedVersions, setSelectedVersions] = useState<Readonly<Record<string, string>>>({});
    const { detailQueries, versionQueries } = useComparedItemsOfType(
        architectureId,
        itemType,
        itemIds,
        selectedVersions,
    );
    const parentIds = useMemo(
        () => uniqueParentIds(detailQueries.map((query) => query.data?.parentId)),
        [detailQueries],
    );
    const parentTitles = useParentTitles(architectureId, itemType, parentIds);

    const items = useMemo(
        (): readonly ComparedItem[] =>
            itemIds.map((itemId, index) => {
                const detailsQuery = detailQueries[index];
                const versionsQuery = versionQueries[index];
                const details = detailsQuery?.data;
                const isVersionPending = versionsQuery?.isLoading === true;

                return {
                    details,
                    error: detailsQuery?.error ?? versionsQuery?.error ?? null,
                    id: itemId,
                    isFetching: detailsQuery?.isFetching === true || versionsQuery?.isFetching === true,
                    isInitialLoading: isVersionPending || detailsQuery?.isLoading === true,
                    parentTitle: is.undefined(details) ? undefined : parentTitles.get(details.parentId),
                    versions: versionsQuery?.data,
                };
            }),
        [detailQueries, itemIds, parentTitles, versionQueries],
    );

    const selectItemVersion = useCallback((itemId: string, version: string): void => {
        setSelectedVersions((currentVersions) => ({ ...currentVersions, [itemId]: version }));
    }, []);

    return { items, selectItemVersion };
};
